import {
  CalendarClock, Mic, TrendingUp, CloudSun, GitCompare, Pill,
  CheckCircle2, CircleAlert, Sparkles, Stethoscope, Crown, Flower2,
} from "lucide-react";

const FEATURES = [
  {
    key: "smart-calendar",
    eyebrow: "Smart Calendar",
    title: "Nudges that arrive before you need them.",
    body: "Rule-based predictions tuned for beekeeping: when to inspect, when to treat for varroa, when the linden flow's about to start, when your queen is overdue for replacement. Each suggestion ships with reasoning so you understand the why.",
    bullets: [
      "Spring varroa check · late-summer mite treatment · winter oxalic dribble",
      "Seasonal forage calendar — northern & southern hemisphere",
      "One-tap convert to a real reminder",
    ],
    visual: <SmartCalendarPreview />,
  },
  {
    key: "voice-journal",
    eyebrow: "Voice-to-Journal",
    title: "Talk through your inspection. We'll write it up.",
    body: "Hit Record while you're working the boxes, describe what you see, and our pipeline (OpenAI Whisper → Claude) transcribes and structures your memo into a complete journal entry. You review, edit, save. Gloves stay on, hands stay free.",
    bullets: [
      "Auto-fills observations, actions, mood, weather, temperature",
      "Tuned vocabulary: brood, supersedure, varroa, capped, propolis",
      "Up to 25 MB per memo — covers a full multi-hive walk-through",
    ],
    visual: <VoiceJournalPreview />,
    reverse: true,
  },
  {
    key: "health-trends",
    eyebrow: "Hive Health Trends",
    title: "Watch your colonies tell their story over time.",
    body: "Twelve-month rollups of every inspection, harvest, treatment and mood reading. Spot the queen that's stalling, the yard that out-produces, the hive that needs a closer look — before it becomes a crisis.",
    bullets: [
      "Per-hive monthly aggregates with SVG charts",
      "Side-by-side compare for 2–4 hives",
      "CSV exports + printable annual report for tax-time",
    ],
    visual: <TrendsPreview />,
  },
  {
    key: "forecast",
    eyebrow: "Weather-aware Forecast",
    title: "Inspect when the bees are happy. Skip the days they aren't.",
    body: "We pull a 5-day forecast for each hive's location and rate every day for inspection suitability — temperature, wind, rain, precipitation chance. No more cracking open a hive at 55°F just to wear the bees out.",
    bullets: [
      "Green / Yellow / Red ratings beekeepers actually use",
      "Geocoded automatically from your hive's location",
      "Cached responses — fast, accurate, free to you",
    ],
    visual: <ForecastPreview />,
    reverse: true,
  },
];

export default function FeatureShowcase() {
  return (
    <section id="spy-features" className="bg-stone-50" data-testid="landing-features">
      <div className="max-w-6xl mx-auto px-6 py-24 sm:py-32 space-y-24">
        <header className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-700 mb-3">What you get</div>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-stone-900">
            Less notebook. More beekeeping.
          </h2>
          <p className="text-stone-600 mt-4 text-base sm:text-lg">
            Every Pro feature built around the question: <em>does this save me time in the apiary, or help me catch a problem sooner?</em>
          </p>
        </header>
        {FEATURES.map((f) => <FeatureRow key={f.key} {...f} />)}
      </div>
    </section>
  );
}

function FeatureRow({ key, eyebrow, title, body, bullets, visual, reverse }) {
  return (
    <div
      className={`grid lg:grid-cols-2 gap-10 lg:gap-14 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}
      data-testid={`feature-row-${key || eyebrow.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="lg:[direction:ltr] space-y-4">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-700">{eyebrow}</div>
        <h3 className="font-display text-3xl sm:text-4xl font-light text-stone-900 leading-tight">{title}</h3>
        <p className="text-stone-600 leading-relaxed">{body}</p>
        <ul className="space-y-2 pt-1">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
              <CheckCircle2 className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:[direction:ltr]">
        {visual}
      </div>
    </div>
  );
}

/* ────────────── Inline UI "screenshots" using the real app styles ────────────── */

function PreviewFrame({ children, testid }) {
  return (
    <div
      className="bg-white border border-stone-200 rounded-3xl shadow-xl shadow-stone-900/5 overflow-hidden p-5"
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-4 px-1">
        <span className="w-2 h-2 rounded-full bg-stone-300" />
        <span className="w-2 h-2 rounded-full bg-stone-300" />
        <span className="w-2 h-2 rounded-full bg-stone-300" />
      </div>
      {children}
    </div>
  );
}

function SmartCalendarPreview() {
  const items = [
    { Icon: Pill, accent: "bg-violet-50 border-violet-200 text-violet-700", title: "Late-summer mite treatment — Linden", pill: "Now", pillColor: "bg-rose-100 text-rose-900 border-rose-200" },
    { Icon: Stethoscope, accent: "bg-sky-50 border-sky-200 text-sky-700", title: "Maple — last inspection 21 days ago", pill: "Soon", pillColor: "bg-amber-100 text-amber-900 border-amber-200" },
    { Icon: Crown, accent: "bg-amber-50 border-amber-200 text-amber-700", title: "Birch — queen is 3 years old", pill: "FYI", pillColor: "bg-stone-100 text-stone-700 border-stone-200" },
  ];
  return (
    <PreviewFrame testid="preview-smart-calendar">
      <div className="space-y-2">
        {items.map((i, idx) => (
          <div key={idx} className={`border ${i.accent.split(" ").slice(0, 2).join(" ")} ${i.accent.split(" ")[0]} rounded-xl p-3 flex items-center gap-3`}>
            <div className={`w-9 h-9 rounded-lg bg-white border ${i.accent.split(" ")[1]} grid place-items-center ${i.accent.split(" ")[2]}`}>
              <i.Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-stone-900 truncate">{i.title}</div>
            </div>
            <span className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded-full border ${i.pillColor}`}>{i.pill}</span>
          </div>
        ))}
      </div>
    </PreviewFrame>
  );
}

function VoiceJournalPreview() {
  return (
    <PreviewFrame testid="preview-voice-journal">
      <div className="border border-amber-200 bg-amber-50/40 rounded-xl p-4 mb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">Pro</span>
            <Sparkles className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-stone-800">Voice to journal</span>
          </div>
          <span className="text-xs font-mono tabular-nums text-rose-700 inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> 00:23
          </span>
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-stone-600">
          <Mic className="w-3.5 h-3.5 text-stone-500" /> Recording…
        </div>
      </div>
      <div className="space-y-2.5 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-stone-500 mb-1">Observations</div>
          <div className="border border-stone-200 rounded-lg p-2.5 text-stone-700">Queen spotted laying nicely. Brood pattern solid across 6 frames. Mite count low.</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-stone-500 mb-1">Actions</div>
          <div className="border border-stone-200 rounded-lg p-2.5 text-stone-700">Added a honey super, reversed brood box.</div>
        </div>
      </div>
    </PreviewFrame>
  );
}

function TrendsPreview() {
  const inspections = [2, 4, 3, 5, 7, 6, 8, 7, 5, 4, 3, 2];
  const harvest = [0, 0, 0, 0, 0, 12, 24, 18, 6, 0, 0, 0];
  const Bar = ({ values, color }) => {
    const max = Math.max(1, ...values);
    return (
      <svg viewBox="0 0 360 80" className="w-full h-20">
        {values.map((v, i) => {
          const h = (v / max) * 70;
          return <rect key={i} x={i * 30 + 4} y={75 - h} width={22} height={h} fill={color} opacity={0.85} rx={2} />;
        })}
      </svg>
    );
  };
  return (
    <PreviewFrame testid="preview-trends">
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: "Inspections", value: 56 },
          { label: "Harvest lb", value: 60 },
          { label: "Treatments", value: 4 },
        ].map((s) => (
          <div key={s.label} className="border border-stone-200 rounded-xl p-2.5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">{s.label}</div>
            <div className="font-display text-xl font-bold text-stone-900 tabular-nums mt-0.5">{s.value}</div>
          </div>
        ))}
      </div>
      <div className="border border-stone-200 rounded-2xl p-4 mb-3">
        <div className="flex items-center gap-2 text-stone-900 font-medium mb-2">
          <TrendingUp className="w-4 h-4 text-sky-600" /> Inspections per month
        </div>
        <Bar values={inspections} color="#0EA5E9" />
      </div>
      <div className="border border-stone-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 text-stone-900 font-medium mb-2">
          <TrendingUp className="w-4 h-4 text-amber-600" /> Harvest (lb) per month
        </div>
        <Bar values={harvest} color="#F59E0B" />
      </div>
    </PreviewFrame>
  );
}

function ForecastPreview() {
  const days = [
    { day: "Mon", high: 78, low: 60, summary: "Sunny", rating: "great" },
    { day: "Tue", high: 82, low: 64, summary: "Mostly clear", rating: "great" },
    { day: "Wed", high: 68, low: 55, summary: "Breezy", rating: "ok" },
    { day: "Thu", high: 59, low: 51, summary: "Rain", rating: "avoid" },
    { day: "Fri", high: 71, low: 58, summary: "Partly cloudy", rating: "ok" },
  ];
  const tone = {
    great: { card: "border-emerald-300 bg-emerald-50/60 ring-emerald-200", pill: "bg-emerald-100 text-emerald-900 border-emerald-200", Icon: CheckCircle2 },
    ok:    { card: "border-amber-300 bg-amber-50/60 ring-amber-200",       pill: "bg-amber-100 text-amber-900 border-amber-200",       Icon: CircleAlert },
    avoid: { card: "border-rose-300 bg-rose-50/60 ring-rose-200",          pill: "bg-rose-100 text-rose-900 border-rose-200",          Icon: CloudSun },
  };
  return (
    <PreviewFrame testid="preview-forecast">
      <div className="flex items-center gap-2 mb-3">
        <CloudSun className="w-5 h-5 text-sky-600" />
        <span className="font-display text-lg font-medium text-stone-900">Inspection forecast</span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {days.map((d) => {
          const t = tone[d.rating];
          return (
            <div key={d.day} className={`border ${t.card} ring-1 rounded-xl p-2.5`}>
              <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-stone-700 mb-1">{d.day}</div>
              <div className="font-display text-xl font-bold text-stone-900 tabular-nums leading-none">{d.high}°</div>
              <div className="text-[10px] text-stone-500 mt-0.5">low {d.low}°</div>
              <div className="text-[10px] text-stone-600 mt-1 capitalize truncate">{d.summary}</div>
              <span className={`mt-1.5 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded-full border ${t.pill}`}>
                <t.Icon className="w-2.5 h-2.5" />
                {d.rating === "avoid" ? "Avoid" : d.rating === "great" ? "Great" : "OK"}
              </span>
            </div>
          );
        })}
      </div>
    </PreviewFrame>
  );
}

// Silence unused-import warnings — components reference these via JSX above
void [CalendarClock, GitCompare, Flower2];
